import React, { useEffect, useState } from "react";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { CRMPageWrap } from "./../../components/Simple/CRMPageWrap/CRMPageWrap";
import { CRMPageBody } from "./../../components/Simple/CRMPageBody/CRMPageBody";
import { CRMLegalMenuBarContainer } from "./../../containers/CRMLegalMenuBarContainer/CRMLegalMenuBarContainer";
import { CRMPadding } from "../../components/Simple/CRMPadding/CRMPadding";
import { CRMSpacingRow } from "../../components/CRMSpacingRow/CRMSpacingRow";
import { SimpleGrid } from "../../components/BuildingBlocks/SimpleGrid";
import { Sticky } from "../../components/BuildingBlocks/Sticky";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMCardOutside } from "../../components/CRMCardOutside/CRMCardOutside";
import { CRMSpacingColumn } from "../../components/CRMSpacingColumn/CRMSpacingColumn";
import { CRMParagraph } from "../../components/Simple/CRMParagraph/CRMParagraph";
import { CRMParagraphBulletPoints } from "../../components/CRMBulletList/CRMParagraphBulletPoints";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { CRMIcon } from "../../components/CRMIcon/CRMIcon";
import CRMInputLabelAndErrorWrapComponent from "../../components/CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";
import { CRMDropdownComponent } from "../../components/CRMDropdownComponent/CRMDropdownComponent";
import { FontQuicksand } from "../../components/BuildingBlocks/FontQuicksand";
import { FontSize } from "../../components/BuildingBlocks/FontSize";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { pipe } from "fp-ts/lib/function";
import { array, either, record } from "fp-ts";
import { DateTime } from "luxon";
import { LegalMIReportType, TLegalMIReportType, getReportTitleAndDescription } from "../../../../domain/codecs/LegalMIReportType";
import { CRMReportDetailsCard } from "../../components/CRMReportDetailsCard/CRMReportDetailsCard";
import { CRMReportTable } from "../../components/CRMReportTable/CRMReportTable";
import { CRMTableContentPlaceholder } from "../../components/Simple/CRMTableContentPlaceholder/CRMTableContentPlaceholder";
import { CRMButtonTertiary } from "../../components/CRMButtonTertiary/CRMButtonTertiary";
import { CRMButtonIcon } from "../../components/CRMButtonIcon/CRMButtonIcon";
import { SpacingRow } from "../../components/BuildingBlocks/SpacingRow";

const SNAPSHOT_REPORTS: Array<TLegalMIReportType> = [
    "bsa-report",
    "legal-referrals-report",
    "property-referrals-report",
    "source-of-wealth-report",
    "dual-rep-report",
    "referral-agreements-report",
    "complaints-report",
    "sar-report",
    "raised-with-insurers-report",
    "unsettled-retentions-report",
];

const isSnapshotReport = (report: TLegalMIReportType): boolean =>
    SNAPSHOT_REPORTS.includes(report);

const RESTRICTED_COMPLIANCE_REPORTS: Array<TLegalMIReportType> = [
    "sar-report",
    "raised-with-insurers-report",
];

const isRestrictedComplianceReport = (report: TLegalMIReportType): boolean =>
    RESTRICTED_COMPLIANCE_REPORTS.includes(report);

export const CRMLegalReportsPage = (props: TContainerStateProps): JSX.Element => {
    const now = new Date();
    const startYear = 2020;
    const currentYear = now.getUTCFullYear();
    const currentMonth = now.getUTCMonth() + 1;
    const yearsSinceStart = currentYear - startYear;
    // There might be a better way to do this, feel free to replace if found - TFC
    const months = [
        ...yearsSinceStart > 0 ? pipe(
            array.range(1, yearsSinceStart),
            array.reduce([], (a: string[], i: number) => [
                ...a,
                ...pipe(
                    array.range(1, 12),
                    array.map((r) => `${startYear + i-1}-${r < 10 ? "0" : ""}${r}`)
                )    
            ])
        ) : [],
        ...pipe(
            array.range(1, currentMonth),
            array.map((r) => `${currentYear}-${r < 10 ? "0" : ""}${r}`)
        ),
    ].reverse();

    const deepLinkedReport = LegalMIReportType.decode(props.state.routes.queryParams.VIEW_CRM_LEGAL_REPORTS.report);

    const [currentSelectedMonth, setCurrentSelectedMonth] = useState<string>(months[0]);
    const [fromMonth, setFromMonth] = useState<string>(months[Math.min(11, months.length - 1)]);
    const [selectedReport, setSelectedReport] = useState<TLegalMIReportType | null>(
        either.isRight(deepLinkedReport) ? deepLinkedReport.right : null
    );
    const [tableData, setTableData] = useState<{ rows: Array<Record<string, unknown>>; headers: Array<string> } | null>(null);
    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);

    const periodQs = `month=${currentSelectedMonth}&from=${fromMonth}&to=${currentSelectedMonth}`;

    const snapshotSelected = selectedReport !== null && isSnapshotReport(selectedReport);

    useEffect(() => {
        if (selectedReport === null) {
            setTableData(null);
            return;
        }
        let cancelled = false;
        setIsLoadingTable(true);
        fetch(`${env.REACT_APP_API_URL}/web/legal/reports/${selectedReport}?${periodQs}&format=json`, {
            credentials: "include",
            headers: { "accept": "application/json", "content-type": "application/json" },
        })
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`report fetch failed: ${res.status}`);
                }
                return res.json();
            })
            .then((data: { rows: Array<Record<string, unknown>>; headers: Array<string> }) => {
                if (!cancelled) {
                    setTableData(data);
                    setIsLoadingTable(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setTableData({ rows: [], headers: [] });
                    setIsLoadingTable(false);
                }
            });
        return () => { cancelled = true; };
    }, [selectedReport, currentSelectedMonth, fromMonth]);

    LegalMIReportType.values
    type ReportDetails = {
        type: TLegalMIReportType;
        title: string;
        description: string;
        helpText: string | null;
    };

    const canManageComplianceReports = props.state.global.user.can_manage_compliance_reports;

    const reportsByLetter = pipe(
        LegalMIReportType.values,
        array.filter((reportType) => canManageComplianceReports || !isRestrictedComplianceReport(reportType)),
        array.map((reportType): ReportDetails => {
            const [title, description, helpText] = getReportTitleAndDescription(reportType);
            return {type: reportType, title, description, helpText};
        }),
        array.reduce({}, (obj: Record<string, Array<ReportDetails>>, reportDetails: ReportDetails) => {
            const firstLetterOfTitle = reportDetails.title.toLowerCase().charAt(0);
            obj[firstLetterOfTitle] = [ ...(Object.prototype.hasOwnProperty.call(obj, firstLetterOfTitle) ? obj[firstLetterOfTitle] : []) , reportDetails].sort((a, b) => a.title.localeCompare(b.title));
            return obj;
        }),
    );

    return (
        <div className="crm-legal-cases">
            <CRMPageWrap>
                {/* HEADER */}
                <CRMLegalMenuBarContainer {...props} />

                {/* BODY */}
                <CRMPageBody>
                    <CRMPadding size="large">
                        <CRMSpacingColumn spacing="large">
                            {/* PAGE TITLE  */}
                            <FontQuicksand>
                                <FontSize size={CRMFontSizes.XX_LARGE}>
                                    <WeightBold>
                                        Download reports for: {fromMonth === currentSelectedMonth
                                            ? DateTime.fromFormat(currentSelectedMonth, "yyyy-MM").toFormat("LLLL yyyy")
                                            : `${DateTime.fromFormat(fromMonth, "yyyy-MM").toFormat("LLLL yyyy")} – ${DateTime.fromFormat(currentSelectedMonth, "yyyy-MM").toFormat("LLLL yyyy")}`}
                                    </WeightBold>
                                </FontSize>
                            </FontQuicksand>
                            {/*  */}
                            <CRMSpacingRow
                                spacing="x-large"
                                alignItems="flex-start"
                                childSize="2fr 1fr"
                            >
                                <CRMSpacingColumn spacing="large">
                                    {selectedReport !== null
                                        ? <CRMSpacingColumn spacing="medium">
                                            <SpacingRow spacing={CRMSpacing.MEDIUM} justifyContent="space-between">
                                                <CRMButtonTertiary label="← Back to reports" onClick={() => setSelectedReport(null)} />
                                                <CRMButtonIcon icon="can-download" href={`${env.REACT_APP_API_URL}/web/legal/reports/${selectedReport}?${periodQs}`} download={true} variant="tertiary" />
                                            </SpacingRow>
                                            <FontQuicksand>
                                                <FontSize size={CRMFontSizes.X_LARGE}>
                                                    <WeightBold>{getReportTitleAndDescription(selectedReport)[0]}</WeightBold>
                                                </FontSize>
                                            </FontQuicksand>
                                            {isLoadingTable
                                                ? <CRMTableContentPlaceholder rowCount={8} />
                                                : tableData !== null
                                                    ? <CRMReportTable headers={tableData.headers} rows={tableData.rows} />
                                                    : <></>}
                                        </CRMSpacingColumn>
                                        : pipe(
                                            reportsByLetter,
                                            record.keys,
                                            (keys) => keys.sort(),
                                            array.map((key) => {
                                                return (
                                                    <React.Fragment key={key}>
                                                    <FontQuicksand>
                                                        <FontSize size={CRMFontSizes.X_LARGE}>
                                                            <WeightBold>
                                                                {key.toUpperCase()}
                                                            </WeightBold>
                                                        </FontSize>
                                                    </FontQuicksand>
                                                    <SimpleGrid templateColumns="1fr 1fr" columnSpacing={CRMSpacing.MEDIUM} rowSpacing={CRMSpacing.MEDIUM}>
                                                        {
                                                            pipe(
                                                                reportsByLetter[key],
                                                                array.mapWithIndex((i, reportDetails) => (
                                                                <CRMReportDetailsCard
                                                                    key={i}
                                                                    title={reportDetails.title}
                                                                    description={reportDetails.description}
                                                                    helpText={reportDetails.helpText}
                                                                    link={`${env.REACT_APP_API_URL}/web/legal/reports/${reportDetails.type}?${periodQs}` }
                                                                    onView={() => setSelectedReport(reportDetails.type)}
                                                                />))
                                                            )
                                                        }
                                                        
                                                    </SimpleGrid>
                                                    </React.Fragment>
                                                )
                                            })
                                        )
                                    }
                                </CRMSpacingColumn>
                                <Sticky top={CRMSpacing.X_LARGE}>
                                    <CRMCardOutside borderRounding="right" shadow={true}>
                                        <CRMPadding size="large">
                                            <CRMSpacingColumn spacing="medium">
                                                <CRMParagraph>
                                                    <CRMParagraphBulletPoints
                                                        bullets={[
                                                            <span>Select the <WeightBold>period (From / To months)</WeightBold> you wish to see reports for below.</span>,
                                                            <span>Browse all reports to the left. They are <WeightBold>alphabetically ordered</WeightBold>.</span>,
                                                            <span>Click the <CRMIcon iconName="can-download" colour="neutral-ink" size="tiny" inline={true}/> button on the ones you want.</span>,
                                                        ]}
                                                    />
                                                
                                                </CRMParagraph>
                                                {selectedReport !== null && isSnapshotReport(selectedReport) &&
                                                    <CRMParagraph>
                                                        <WeightBold>{getReportTitleAndDescription(selectedReport)[0]}</WeightBold> is a snapshot of the current position, so the period below doesn’t apply to it.
                                                    </CRMParagraph>
                                                }
                                                <CRMInputLabelAndErrorWrapComponent label="From month">
                                                    <CRMDropdownComponent
                                                        value={fromMonth}
                                                        options={months.map((month) => ({
                                                            value: month,
                                                            label: DateTime.fromFormat(month, "yyyy-MM").toFormat("LLLL yyyy"),
                                                        }))}
                                                        onChange={setFromMonth}
                                                        displayError={false}
                                                        disabled={snapshotSelected}
                                                    />
                                                </CRMInputLabelAndErrorWrapComponent>
                                                <CRMInputLabelAndErrorWrapComponent label="To month">
                                                    <CRMDropdownComponent
                                                        value={currentSelectedMonth}
                                                        options={months.map((month) => ({
                                                            value: month,
                                                            label: DateTime.fromFormat(month, "yyyy-MM").toFormat("LLLL yyyy"),
                                                        }))}
                                                        onChange={setCurrentSelectedMonth}
                                                        displayError={false}
                                                        disabled={snapshotSelected}
                                                    />
                                                </CRMInputLabelAndErrorWrapComponent>
                                            </CRMSpacingColumn>
                                        </CRMPadding>
                                    </CRMCardOutside>
                                </Sticky>
                            </CRMSpacingRow>
                        </CRMSpacingColumn>
                    </CRMPadding>
                </CRMPageBody>
            </CRMPageWrap>
        </div>
    );
}
